import React from "react";
import { CheckCircleFill } from "../icons";
import { useTranslation, Trans } from "react-i18next";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { useUsesDesign } from "../context/GuestCheckoutSettingsContext";

interface PaymentSuccessCardProps {
  depositTotal: string | number | null;
  currency: string;
  countryCode: string | null;
  isAuthentication: boolean;
  /**
   * Whether the pre-order was actually paid for (as opposed to pay-at-venue).
   * Only meaningful alongside `bookingJourney` + `isAuthentication`.
   */
  preorderPaymentTaken?: boolean;
  mode?: "successOnly" | "successWithDetails" | "full" | "bookingWithPreorders";
  shouldHidePrices?: boolean;
  email?: string;
  isPreorderCompletion?: boolean;
  /**
   * Booking-journey confirmations (/confirm for /book) have no lead booker, so
   * the order message omits the email and "lead booker" guidance shown for the
   * place-your-order flow.
   */
  bookingJourney?: boolean;
}

const PaymentSuccessCard: React.FC<PaymentSuccessCardProps> = ({
  depositTotal,
  currency,
  countryCode,
  isAuthentication,
  preorderPaymentTaken,
  mode,
  shouldHidePrices,
  email,
  isPreorderCompletion,
  bookingJourney,
}) => {
  const { t } = useTranslation();
  const usesDesign = useUsesDesign();

  const depositAmount = typeof depositTotal === "string" ? Number.parseFloat(depositTotal) : (depositTotal ?? 0);
  const formattedAmount = formatCurrency(depositAmount, currency, countryCode ?? "GB");

  const getHeading = (): React.ReactNode => {
    if (isPreorderCompletion) {
      return <h2 className="gcw:text-subtitle gcw:mb-0">{t("order_received", "Order Received")}</h2>;
    }
    if (mode === "successOnly" || mode === "full") {
      return <h2 className="gcw:text-subtitle gcw:mb-0">{t("payment_successful_title", "Payment Successful")}</h2>;
    }
    return <h2 className="gcw:text-subtitle gcw:mb-0">{t("order_received", "Order Received")}</h2>;
  };

  const getSuccessMessage = (): React.ReactNode => {
    if (isPreorderCompletion) {
      return getPreorderMessage();
    }

    if (mode === "successWithDetails") {
      return getOrderSuccessMessage();
    }

    if (mode === "successOnly") {
      return getPaymentSuccessMessage();
    }

    return null;
  };

  const getPreorderMessage = (): React.ReactNode => {
    if (depositAmount > 0) {
      return (
        <Trans
          i18nKey="preorder_complete_with_payment"
          values={{ amount: formattedAmount }}
          components={{ strong: <strong /> }}
        />
      );
    }
    return t("preorder_complete_no_payment");
  };

  const getOrderSuccessMessage = (): React.ReactNode => {
    if (bookingJourney) {
      // A pay-at-venue pre-order (no payment taken) alongside an authenticate
      // deposit is not a payment at all — reuse the existing authenticate
      // wording instead of wrongly claiming "a payment has been taken".
      const i18nKey =
        isAuthentication && !preorderPaymentTaken ? "booking_summary_preorder_auth" : "order_success_booking_journey";
      return <Trans i18nKey={i18nKey} values={{ amount: formattedAmount }} components={{ strong: <strong /> }} />;
    }
    if (shouldHidePrices) {
      return <Trans i18nKey="order_success_hide_price" values={{ email: email }} components={{ strong: <strong /> }} />;
    }
    return (
      <Trans
        i18nKey="order_success"
        values={{ amount: formattedAmount, email: email }}
        components={{ strong: <strong /> }}
      />
    );
  };

  const getPaymentSuccessMessage = (): React.ReactNode => {
    const i18nKey = isAuthentication ? "payment_successful_message_auth" : "payment_successful_message_payment";

    return <Trans i18nKey={i18nKey} values={{ amount: formattedAmount }} components={{ strong: <strong /> }} />;
  };

  return (
    <div className="gcw:rounded-lg gcw:border gcw:border-gray-200 gcw:bg-white gcw:shadow-sm payment-success-card gcw:mb-4">
      <div className="gcw:p-bs-3">
        <div className="gcw:flex gcw:items-center gcw:mb-2">
          {!usesDesign && <CheckCircleFill size={24} className="gcw:text-info gcw:me-2" aria-hidden="true" />}
          {getHeading()}
        </div>
        <p className="gcw:mb-0 gcw:text-ref">{getSuccessMessage()}</p>
        {mode === "successOnly" && !isPreorderCompletion && (
          <p className="gcw:mb-0 gcw:mt-2 gcw:text-ref">
            {t("payment_confirmation_email_notice", "A confirmation email will be sent to you shortly.")}
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessCard;
